/**
 * Mounted once at app shell level. Watches the scrapeJobs slice for
 * transitions to terminal states and surfaces a toast so the user sees
 * the outcome immediately — including the carrier's actual error
 * message ("We couldn't find the claimant") on validation failures.
 *
 * For FTC personnel and super admins only (viewerRole). Office staff never
 * see the agent's results this way: a failure is ours to act on, and they
 * hear from a person (Details Needed by Smilepass) or see the result.
 *
 * Once a job is toasted, its id is remembered for the lifetime of this
 * tab so we don't re-toast on every BULK_SYNC. We also skip jobs whose
 * terminal timestamp is older than 60s on first sight, to avoid spamming
 * a wall of toasts when the user reloads after the fact.
 *
 * react-redux 6.x — uses connect().
 */

import React from "react";
import { connect } from "react-redux";
import { message } from "antd";

import { loadViewerRole } from "../viewerRole";

const TERMINAL_FAILED = new Set(["failed", "timed_out", "cancelled"]);
const TERMINAL_SUCCESS = new Set(["succeeded"]);
const RECENT_WINDOW_MS = 60 * 1000;

function jobLabel(job) {
  const op = (job.operation_type || "").replace(/_/g, " ");
  return op ? op.charAt(0).toUpperCase() + op.slice(1) : "Insurance fetch";
}

function patientLabel(job) {
  return job.patient_name || (job.patient_id ? "patient #" + job.patient_id : "this patient");
}

function isRecent(job) {
  const ts = job.completed_at || job.updated_at;
  if (!ts) return true; // assume just-now if missing
  const t = new Date(ts).getTime();
  if (Number.isNaN(t)) return true;
  return Date.now() - t < RECENT_WINDOW_MS;
}

export class ScrapeJobToastNotifierInner extends React.Component {
  constructor(props) {
    super(props);
    // Snapshot the jobs we've seen at mount so we don't toast for jobs
    // that finished before the tab opened. Only NEW transitions get a toast.
    this.seen = new Set();
    Object.keys(props.byId || {}).forEach((id) => {
      const j = props.byId[id];
      if (j && (TERMINAL_FAILED.has(j.state) || TERMINAL_SUCCESS.has(j.state))) {
        this.seen.add(id);
      }
    });
    // Who is looking. Nothing is toasted — or marked seen — until the role
    // answers, so an FTC still gets a toast that landed during the wait
    // (within the 60 s window), and office staff never get one.
    this.state = { viewer: null };
  }

  componentDidMount() {
    this.alive = true;
    loadViewerRole().then((viewer) => {
      if (this.alive) this.setState({ viewer });
    });
  }

  componentWillUnmount() {
    this.alive = false;
  }

  componentDidUpdate() {
    const { viewer } = this.state;
    if (!viewer) return;
    const byId = this.props.byId || {};
    Object.keys(byId).forEach((id) => {
      if (this.seen.has(id)) return;
      const job = byId[id];
      if (!job) return;
      const failed = TERMINAL_FAILED.has(job.state);
      const succeeded = TERMINAL_SUCCESS.has(job.state);
      if (!failed && !succeeded) return;
      if (!viewer.isFtc || !isRecent(job)) {
        this.seen.add(id);
        return;
      }
      if (failed) {
        const tail = job.error_message ? ' — ' + job.error_message : "";
        message.error({
          content: jobLabel(job) + " failed for " + patientLabel(job) + tail,
          duration: 8,
          key: "scrapejob_" + id, // dedupe if Redux fires twice
        });
      } else {
        message.success({
          content: jobLabel(job) + " completed for " + patientLabel(job),
          duration: 4,
          key: "scrapejob_" + id,
        });
      }
      this.seen.add(id);
    });
  }

  render() {
    return null;
  }
}

const mapStateToProps = (state) => ({
  byId: (state.scrapeJobs && state.scrapeJobs.byId) || {},
});

export default connect(mapStateToProps)(ScrapeJobToastNotifierInner);
